import { getFamilySlugForHost } from "./families";

/**
 * Tiny localStorage cache for the family logo URLs. The Logo component
 * reads it synchronously on mount so the first paint already has the
 * right image — no flash of the bundled fallback before the API call
 * returns.
 *
 * Signed GCS URLs have a 5 min TTL on the server. We cache for 4 min so
 * a stale URL never hits the browser. Even if an expired URL slipped
 * through, the <img onError> in Logo falls back to the bundled image.
 */
const CACHE_KEY_PREFIX = "branding-cache:";
const MAX_AGE_MS = 4 * 60 * 1000;

interface CachedBranding {
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  cachedAt: number;
}

function key(slug: string): string {
  return `${CACHE_KEY_PREFIX}${slug}`;
}

export function readCachedBranding(
  slug: string | null = getFamilySlugForHost(),
): CachedBranding | null {
  if (!slug) return null;
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(key(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedBranding;
    if (typeof parsed.cachedAt !== "number") return null;
    if (Date.now() - parsed.cachedAt > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeCachedBranding(
  slug: string | null,
  branding: { logoLightUrl: string | null; logoDarkUrl: string | null },
): void {
  if (!slug) return;
  if (typeof window === "undefined") return;

  // Skip writing when both URLs are null — the consumer can fall back to
  // the bundled image without a cache miss penalty.
  if (!branding.logoLightUrl && !branding.logoDarkUrl) {
    try {
      window.localStorage.removeItem(key(slug));
    } catch {
      // Silently ignored — quota / private mode shouldn't break rendering.
    }
    return;
  }

  try {
    window.localStorage.setItem(
      key(slug),
      JSON.stringify({
        logoLightUrl: branding.logoLightUrl,
        logoDarkUrl: branding.logoDarkUrl,
        cachedAt: Date.now(),
      }),
    );
  } catch {
    // Silently ignored.
  }
}
